import React, { useState, useEffect, useCallback, useRef } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { savedItemsApi } from '../lib/api';
import { usePrinter } from '../lib/usePrinter';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
    Plus, Trash2, Edit2, X, Search, Barcode, Printer, Loader2, Bluetooth,
    BluetoothOff, BluetoothConnected, WifiOff, Thermometer, Download
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import JsBarcode from 'jsbarcode';

const LOCATION_TYPES = [
    { id: 'fridge', label: 'Fridge', defaultRange: '0 to 5°C' },
    { id: 'freezer', label: 'Freezer', defaultRange: '-25 to -18°C' },
    { id: 'hot_hold', label: 'Hot Hold', defaultRange: '63 to 100°C' },
    { id: 'delivery', label: 'Delivery', defaultRange: '-5 to 8°C' },
    { id: 'other', label: 'Other', defaultRange: 'Custom' },
];

const LABEL_WIDTH = 400;
const LABEL_HEIGHT = 240;

export default function SavedItems() {
    const { isManagerOrAdmin } = useAuth();
    const printer = usePrinter();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [search, setSearch] = useState('');
    const [printingId, setPrintingId] = useState(null);
    const [barcodePreview, setBarcodePreview] = useState(null);

    const [form, setForm] = useState({ name: '', location_type: '', min_temp: '', max_temp: '' });

    const loadItems = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await savedItemsApi.getAll();
            setItems(data);
        } catch {
            toast.error('Failed to load saved items');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadItems(); }, [loadItems]);

    const resetForm = () => {
        setForm({ name: '', location_type: '', min_temp: '', max_temp: '' });
        setEditingId(null);
        setShowForm(false);
    };

    const handleSave = async () => {
        if (!form.name.trim()) { toast.error('Item name is required'); return; }
        if (!form.location_type) { toast.error('Location type is required'); return; }
        try {
            const payload = {
                name: form.name,
                location_type: form.location_type,
                min_temp: form.min_temp !== '' ? parseFloat(form.min_temp) : null,
                max_temp: form.max_temp !== '' ? parseFloat(form.max_temp) : null,
            };
            if (editingId) {
                await savedItemsApi.update(editingId, payload);
                toast.success('Item updated');
            } else {
                await savedItemsApi.create(payload);
                toast.success('Item created');
            }
            resetForm();
            loadItems();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to save');
        }
    };

    const handleEdit = (item) => {
        setForm({
            name: item.name,
            location_type: item.location_type,
            min_temp: item.min_temp != null ? String(item.min_temp) : '',
            max_temp: item.max_temp != null ? String(item.max_temp) : '',
        });
        setEditingId(item.id);
        setShowForm(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this saved item?')) return;
        try {
            await savedItemsApi.delete(id);
            toast.success('Deleted');
            loadItems();
        } catch {
            toast.error('Failed to delete');
        }
    };

    const renderBarcodeCanvas = useCallback((item) => {
        const canvas = document.createElement('canvas');
        canvas.width = LABEL_WIDTH;
        canvas.height = LABEL_HEIGHT;
        const ctx = canvas.getContext('2d');

        // White background
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, LABEL_WIDTH, LABEL_HEIGHT);
        ctx.fillStyle = '#000000';

        const halfH = LABEL_HEIGHT / 2;

        // Top 50%: Item name, centered
        ctx.font = '900 32px Arial, sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(item.name.toUpperCase(), LABEL_WIDTH / 2, halfH / 2, LABEL_WIDTH - 40);

        // Bottom 50%: Code 128 barcode
        const barcodeCanvas = document.createElement('canvas');
        try {
            JsBarcode(barcodeCanvas, item.barcode_value, {
                format: 'CODE128',
                width: 2,
                height: 80,
                displayValue: true,
                fontSize: 14,
                margin: 0,
                textMargin: 2,
            });
            // Draw barcode scaled to full width of bottom half
            const barcodeH = halfH;
            const barcodeW = LABEL_WIDTH - 20;
            ctx.drawImage(barcodeCanvas, 10, halfH, barcodeW, barcodeH);
        } catch {
            ctx.font = '16px Arial';
            ctx.fillText(item.barcode_value, LABEL_WIDTH / 2, halfH + 40);
        }

        return canvas;
    }, []);

    const handlePrintBarcode = async (item) => {
        setPrintingId(item.id);
        try {
            if (printer.connected) {
                const canvas = renderBarcodeCanvas(item);
                const { ImageEncoder } = await import('@mmote/niimbluelib');
                const encoded = ImageEncoder.encodeCanvas(canvas, 'none');
                const printTask = printer.connected && window.__niimbotClient
                    ? window.__niimbotClient.abstraction.newPrintTask('B1', { totalPages: 1, statusPollIntervalMs: 100, statusTimeoutMs: 10000, pageTimeoutMs: 10000 })
                    : null;

                // Use the printer hook's BLE print with custom canvas
                const result = await printer.printViaBle({
                    _customCanvas: canvas,
                    item_name: item.name,
                    barcode_value: item.barcode_value,
                });
                if (result.success) {
                    toast.success('Barcode label printed!');
                } else {
                    toast.error(`Print failed: ${result.error}`);
                }
            } else {
                // Browser print fallback
                setBarcodePreview(item);
                setTimeout(() => window.print(), 300);
            }
        } catch {
            toast.error('Failed to print barcode');
        } finally {
            setPrintingId(null);
        }
    };

    const handleConnect = async () => {
        const result = await printer.connect();
        if (result.success) {
            toast.success(`Connected to ${result.deviceName || 'printer'}`);
        } else if (result.error !== 'Pairing cancelled') {
            toast.error(result.error);
        }
    };

    const handleDownloadBarcode = (item) => {
        const canvas = renderBarcodeCanvas(item);
        const link = document.createElement('a');
        link.download = `${item.barcode_value}.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
    };

    const handleLocationChange = (val) => {
        setForm(prev => {
            const defaults = { fridge: [0, 5], freezer: [-25, -18], hot_hold: [63, 100], delivery: [-5, 8] };
            const range = defaults[val];
            return {
                ...prev,
                location_type: val,
                min_temp: range ? String(range[0]) : prev.min_temp,
                max_temp: range ? String(range[1]) : prev.max_temp,
            };
        });
    };

    const filtered = items.filter(i => i.name.toLowerCase().includes(search.toLowerCase()));

    return (
        <Layout>
            <div data-testid="saved-items-page">
                {/* Header */}
                <div className="flex flex-col gap-3 mb-6">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div>
                            <h1 className="text-2xl font-bold text-slate-100 flex items-center gap-2">
                            <Barcode className="h-6 w-6 brand-icon" /> Appliances
                            </h1>
                            <p className="text-slate-400 text-sm mt-1">Manage temperature check locations with barcode labels</p>
                        </div>
                        {isManagerOrAdmin && (
                            <Button onClick={() => { resetForm(); setShowForm(true); }} className="brand-btn font-medium" data-testid="add-saved-item-button">
                                <Plus className="h-4 w-4 mr-1" /> New Item
                            </Button>
                        )}
                    </div>

                    {/* Printer Status Bar */}
                    <div className={cn(
                        "flex items-center gap-3 p-3 rounded-lg border text-sm",
                        printer.connected ? "bg-emerald-500/10 border-emerald-500/30" : "bg-slate-800/50 border-slate-700"
                    )} data-testid="printer-status-bar">
                        {printer.connected ? (
                            <>
                                <BluetoothConnected className="h-4 w-4 text-emerald-400 shrink-0" />
                                <span className="text-emerald-400 font-medium flex-1 truncate">{printer.deviceName || 'NIIMBOT B1'} — Ready</span>
                                <Button size="sm" variant="outline" onClick={() => printer.disconnect()} className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 text-xs h-7 shrink-0" data-testid="disconnect-printer-button">
                                    <BluetoothOff className="h-3 w-3 mr-1" /> Disconnect
                                </Button>
                            </>
                        ) : printer.bleSupported ? (
                            <>
                                <Bluetooth className="h-4 w-4 text-slate-400 shrink-0" />
                                <span className="text-slate-400 flex-1">No printer — labels will use browser print</span>
                                <Button size="sm" onClick={handleConnect} disabled={printer.connecting} className="bg-blue-600 hover:bg-blue-700 text-white text-xs h-7 shrink-0" data-testid="connect-printer-button">
                                    {printer.connecting ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Connecting...</> : <><Bluetooth className="h-3 w-3 mr-1" /> Connect NIIMBOT</>}
                                </Button>
                            </>
                        ) : (
                            <>
                                <WifiOff className="h-4 w-4 text-slate-500 shrink-0" />
                                <span className="text-slate-500 flex-1">Web Bluetooth not available — using browser print</span>
                            </>
                        )}
                    </div>
                </div>

                {/* Search */}
                <div className="relative mb-4">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                    <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search items..."
                        className="pl-9 bg-slate-800 border-slate-700 text-slate-100" data-testid="search-saved-items" />
                </div>

                {/* Form */}
                {showForm && (
                    <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-5 mb-6" data-testid="saved-item-form">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-lg font-semibold text-slate-200">{editingId ? 'Edit Item' : 'New Saved Item'}</h2>
                            <Button variant="ghost" size="icon" onClick={resetForm} className="text-slate-400 h-7 w-7"><X className="h-4 w-4" /></Button>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <Label className="text-slate-300">Item Name *</Label>
                                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                                    className="mt-1 bg-slate-800 border-slate-700 text-slate-100" placeholder="e.g. Main Fridge" data-testid="saved-item-name" />
                            </div>
                            <div>
                                <Label className="text-slate-300">Location Type *</Label>
                                <Select value={form.location_type} onValueChange={handleLocationChange}>
                                    <SelectTrigger className="mt-1 bg-slate-800 border-slate-700 text-slate-100" data-testid="saved-item-location-type">
                                        <SelectValue placeholder="Select type" />
                                    </SelectTrigger>
                                    <SelectContent className="bg-slate-900 border-slate-800">
                                        {LOCATION_TYPES.map(t => (
                                            <SelectItem key={t.id} value={t.id} className="text-slate-200 focus:bg-slate-800">
                                                {t.label} ({t.defaultRange})
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="text-slate-300">Min Temp (°C)</Label>
                                <Input type="number" step="0.1" value={form.min_temp} onChange={(e) => setForm({ ...form, min_temp: e.target.value })}
                                    className="mt-1 bg-slate-800 border-slate-700 text-slate-100" placeholder="e.g. 0" data-testid="saved-item-min-temp" />
                            </div>
                            <div>
                                <Label className="text-slate-300">Max Temp (°C)</Label>
                                <Input type="number" step="0.1" value={form.max_temp} onChange={(e) => setForm({ ...form, max_temp: e.target.value })}
                                    className="mt-1 bg-slate-800 border-slate-700 text-slate-100" placeholder="e.g. 5" data-testid="saved-item-max-temp" />
                            </div>
                        </div>
                        <div className="flex gap-2 mt-4">
                            <Button onClick={handleSave} className="brand-btn font-medium" data-testid="save-saved-item-button">
                                {editingId ? 'Update Item' : 'Create Item'}
                            </Button>
                            <Button variant="outline" onClick={resetForm} className="border-slate-700 text-slate-300">Cancel</Button>
                        </div>
                    </div>
                )}

                {/* Items Grid */}
                {loading ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {[1, 2, 3].map(i => <div key={i} className="h-48 bg-slate-900/60 rounded-xl animate-pulse" />)}
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="text-center py-16 text-slate-500">
                        <Barcode className="h-12 w-12 mx-auto mb-3 opacity-30" />
                        <p>No saved items yet</p>
                        <p className="text-xs mt-1">Create items for locations you check temperatures on</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {filtered.map((item) => (
                            <SavedItemCard
                                key={item.id}
                                item={item}
                                isManagerOrAdmin={isManagerOrAdmin}
                                onEdit={handleEdit}
                                onDelete={handleDelete}
                                onPrint={handlePrintBarcode}
                                onDownload={handleDownloadBarcode}
                                printingId={printingId}
                                printerConnected={printer.connected}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* Print barcode preview for browser print */}
            {barcodePreview && (
                <div className="print-barcode-label" id="print-barcode-target">
                    <BarcodePreviewContent item={barcodePreview} onClose={() => setBarcodePreview(null)} />
                </div>
            )}
        </Layout>
    );
}

function SavedItemCard({ item, isManagerOrAdmin, onEdit, onDelete, onPrint, onDownload, printingId, printerConnected }) {
    const canvasRef = useRef(null);

    useEffect(() => {
        if (canvasRef.current) {
            try {
                JsBarcode(canvasRef.current, item.barcode_value, {
                    format: 'CODE128', width: 1.5, height: 40, displayValue: true,
                    fontSize: 11, margin: 2, textMargin: 1, background: 'transparent',
                    lineColor: '#94a3b8',
                });
            } catch { /* barcode render fail */ }
        }
    }, [item.barcode_value]);

    const locType = LOCATION_TYPES.find(t => t.id === item.location_type);

    return (
        <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-4 flex flex-col" data-testid={`saved-item-${item.id}`}>
            <div className="flex items-start justify-between mb-2">
                <div>
                    <h3 className="text-base font-semibold text-slate-200">{item.name}</h3>
                    <span className="text-xs text-slate-500">{locType?.label || item.location_type}</span>
                </div>
                {isManagerOrAdmin && (
                    <div className="flex gap-0.5">
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-slate-400 hover:text-slate-200"
                            onClick={() => onEdit(item)} data-testid={`edit-saved-item-${item.id}`}>
                            <Edit2 className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-slate-400 hover:text-rose-400"
                            onClick={() => onDelete(item.id)} data-testid={`delete-saved-item-${item.id}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                    </div>
                )}
            </div>

            {/* Temperature Range */}
            <div className="flex items-center gap-2 text-xs text-slate-400 mb-2">
                <Thermometer className="h-3 w-3" />
                <span>
                    {item.min_temp != null && item.max_temp != null
                        ? `${item.min_temp}°C to ${item.max_temp}°C`
                        : 'No range set'}
                </span>
            </div>

            {/* Barcode Preview */}
            <div className="bg-slate-950/50 rounded-lg p-2 mb-3 flex justify-center">
                <canvas ref={canvasRef} data-testid={`barcode-preview-${item.id}`} />
            </div>

            {/* Actions */}
            <div className="mt-auto flex gap-2">
                <Button onClick={() => onPrint(item)} disabled={!!printingId}
                    className={cn("flex-1 font-medium h-9 text-sm",
                        printerConnected ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-slate-700 hover:bg-slate-600 text-slate-200"
                    )} data-testid={`print-barcode-${item.id}`}>
                    {printingId === item.id ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Printing...</>
                        : <><Printer className="h-4 w-4 mr-1.5" /> Print Label</>}
                </Button>
                <Button variant="outline" size="icon" className="h-9 w-9 border-slate-700 text-slate-400 hover:text-slate-200"
                    onClick={() => onDownload(item)} data-testid={`download-barcode-${item.id}`}>
                    <Download className="h-4 w-4" />
                </Button>
            </div>
        </div>
    );
}

function BarcodePreviewContent({ item, onClose }) {
    const canvasRef = useRef(null);

    useEffect(() => {
        if (canvasRef.current) {
            try {
                JsBarcode(canvasRef.current, item.barcode_value, {
                    format: 'CODE128', width: 2, height: 80, displayValue: true,
                    fontSize: 14, margin: 4, textMargin: 2,
                });
            } catch { /* fail */ }
        }
    }, [item.barcode_value]);

    return (
        <div style={{ width: '50mm', height: '30mm', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', background: 'white', color: 'black' }}>
            <div style={{ fontWeight: 900, fontSize: '16px', textAlign: 'center', height: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {item.name.toUpperCase()}
            </div>
            <div style={{ height: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <canvas ref={canvasRef} style={{ maxWidth: '100%', maxHeight: '100%' }} />
            </div>
        </div>
    );
}

const LOCATION_TYPES_EXPORT = LOCATION_TYPES;
export { LOCATION_TYPES_EXPORT as LOCATION_TYPES };
